/**
 * 日付処理ユーティリティ
 *
 * DRY原則に基づき、プロジェクト全体で使用される日付処理関数を集約。
 * 主な用途：
 * - 日付の時刻リセット（0:00:00.000）
 * - 日付比較（同一日判定、今日判定）
 * - 日付演算（加算、減算）
 */

/**
 * 日付の時刻を0:00:00.000にリセットした新しいDateオブジェクトを返す
 * @param date 対象の日付
 * @returns 時刻がリセットされた新しいDateオブジェクト
 */
export const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * 2つの日付が同じ日かどうかを判定
 * @param date1 比較対象の日付1
 * @param date2 比較対象の日付2
 * @returns 同じ日の場合true
 */
export const isSameDate = (date1: Date, date2: Date): boolean => {
  const d1 = normalizeDate(date1);
  const d2 = normalizeDate(date2);
  return d1.getTime() === d2.getTime();
};

/**
 * 指定した日付が「今日」かどうかを判定
 * @param date 判定対象の日付
 * @param today 「今日」の日付（useCurrentDate() や getCurrentDate() で取得）
 * @returns 今日の場合true
 */
export const isToday = (date: Date, today: Date): boolean => {
  return isSameDate(date, today);
};

/**
 * 日付に指定した日数を加算した新しいDateオブジェクトを返す
 * @param date 基準日
 * @param days 加算する日数（負の値で減算）
 * @returns 加算後の新しいDateオブジェクト
 */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * 日付が指定した範囲内かどうかを判定
 * @param date 判定対象の日付
 * @param startDate 範囲の開始日
 * @param endDate 範囲の終了日
 * @returns 範囲内の場合true（境界を含む）
 */
export const isDateInRange = (date: Date, startDate: Date, endDate: Date): boolean => {
  const normalized = normalizeDate(date);
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);
  return normalized >= start && normalized <= end;
};

/**
 * 日付配列を生成（開始日から指定日数分）
 * @param startDate 開始日
 * @param days 日数
 * @returns 日付の配列
 */
export const generateDateRange = (startDate: Date, days: number): Date[] => {
  const dates: Date[] = [];
  const start = normalizeDate(startDate);

  for (let i = 0; i < days; i++) {
    dates.push(addDays(start, i));
  }

  return dates;
};

/**
 * 2つの日付間の日数を計算
 * @param date1 日付1
 * @param date2 日付2
 * @returns 日数（date2 - date1）
 */
export const daysBetween = (date1: Date, date2: Date): number => {
  const d1 = normalizeDate(date1);
  const d2 = normalizeDate(date2);
  const diffTime = d2.getTime() - d1.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * 日本語曜日の配列
 */
export const WEEKDAY_LABELS_JA = ['日', '月', '火', '水', '木', '金', '土'] as const;

/**
 * 日付から曜日（日本語）を取得
 * @param date 対象の日付
 * @returns 曜日（日、月、火、水、木、金、土）
 */
export const getWeekdayJa = (date: Date): string => {
  return WEEKDAY_LABELS_JA[date.getDay()];
};

/**
 * 日付を YYYY/MM/DD 形式でフォーマット
 * @param date 対象の日付
 * @returns フォーマットされた日付文字列
 */
export const formatDateYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

/**
 * 日付を YYYY/MM/DD 曜 形式でフォーマット
 * @param date 対象の日付
 * @returns フォーマットされた日付文字列（曜日付き）
 */
export const formatDateWithWeekday = (date: Date): string => {
  return `${formatDateYMD(date)} ${getWeekdayJa(date)}`;
};

/**
 * 日付を YYYY-MM-DD 形式でフォーマット（input要素向け）
 * @param date 対象の日付
 * @returns フォーマットされた日付文字列
 */
export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 日付を YYYY年M月D日 形式でフォーマット（日本語表示向け）
 * @param date 対象の日付
 * @returns フォーマットされた日付文字列
 */
export const formatDateJa = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${year}年${month}月${day}日`;
};

/**
 * 日付を M/D 形式でフォーマット（短縮表示向け）
 * @param date 対象の日付
 * @returns フォーマットされた日付文字列
 */
export const formatDateMD = (date: Date): string => {
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

/**
 * 日付を MM/DD 形式でフォーマット（ゼロ埋め）
 * @param date 対象の日付
 * @returns フォーマットされた日付文字列
 */
export const formatDateMMDD = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

// isToday(date, today) は純粋関数。呼び出し元で today を取得すること。

/**
 * 日付を `[min, max]` の閉区間にクランプする (両端含む)。
 *
 * - `date < min` なら `min` を返す
 * - `date > max` なら `max` を返す
 * - 範囲内ならそのまま返す
 *
 * 主用途: 除草剤散布モーダルの初期日付フォールバック (今日が `[3/1, 8/31]` 範囲外なら 3/1 か 8/31 に寄せる)。
 *
 * @param date クランプ対象の日付
 * @param min 下限 (両端含む)
 * @param max 上限 (両端含む)
 * @returns クランプ後の日付
 */
export const clampToRange = (date: Date, min: Date, max: Date): Date => {
  if (date.getTime() < min.getTime()) return min;
  if (date.getTime() > max.getTime()) return max;
  return date;
};
